import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  LoadingSpinner,
  NotificationErrorIcon,
  NotificationInfoIcon,
  NotificationSuccessIcon
} from 'edenred-ui';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { useOnboardingPayment, useOnboardingWizard } from '@epi-shared/hooks';
import { TopupFormType } from '@epi-pages/LoadBenefit/components/Topup/TopupForm';
import { FormFields } from '@epi-forms/helpers';
import {
  TopupFormStatuses,
  TopupFormStatusesType
} from '@epi-constants/topupForm';

import { SemiBold } from '../TopupConfirmationModal.styles';

interface OnboardingContentProps {
  isTopupPending: boolean;
  handleCloseModal: () => void;
  topupRequestStatus: TopupFormStatusesType | null;
  resetTopupStatus: () => void;
}

export const OnboardingContent = ({
  isTopupPending,
  handleCloseModal,
  topupRequestStatus,
  resetTopupStatus
}: OnboardingContentProps) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const benefitType = watch(FormFields.Benefit);
  const { fontSemiBold, sizeExtraExtraLarge, fontSize, gray100 } = useTheme();
  const { reset } = useFormContext<TopupFormType>();

  const onboardingPayment = useOnboardingPayment(reset);
  const { refetchFlags } = useOnboardingWizard();

  const isSuccess = topupRequestStatus === TopupFormStatuses.SUCCESS;
  const isFail = topupRequestStatus === TopupFormStatuses.FAIL;

  const handleNext = () => {
    if (isSuccess && onboardingPayment?.onboardingLoadBenefit) {
      reset();
      handleCloseModal();
      resetTopupStatus();
      onboardingPayment.onboardingLoadBenefit.onNext();
    } else {
      reset();
      resetTopupStatus();
      handleCloseModal();
    }
  };

  useEffect(() => {
    refetchFlags();
  }, [topupRequestStatus]);

  if (isTopupPending) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        id="topup-onboarding_modal"
        justifyContent="space-between"
      >
        <Dialog.Title>
          <Box display={'flex'} justifyContent={'flex-start'} gap={2}>
            <NotificationInfoIcon />
            <Box
              id="topup-confirmation_modal-title"
              fontSize={sizeExtraExtraLarge}
              fontWeight={fontSemiBold}
              color={gray100}
            >
              {t('containers.deposit_money.topup_tab.confirmation_modal.title')}
            </Box>
          </Box>
        </Dialog.Title>
        <Dialog.Content>
          <Box minHeight={'100px'}></Box>
          <LoadingSpinner />
        </Dialog.Content>
        <Dialog.Actions>
          <Box
            display={'flex'}
            justifyContent={'end'}
            gap={2}
            width="100%"
            pl={6}
            pr={1}
          >
            <Button disabled={true} id="topup-confirmation_confirm">
              {t(
                'containers.deposit_money.topup_tab.confirmation_modal.buttons.confirm'
              )}
            </Button>
          </Box>
        </Dialog.Actions>
      </Box>
    );
  }
  return (
    <Box
      display="flex"
      flexDirection="column"
      id="topup-onboarding_modal"
      justifyContent="space-between"
    >
      {isSuccess && (
        <Box>
          <Dialog.Title>
            <Box display="flex" flexDirection="column" alignItems="start">
              <Box
                fontSize={sizeExtraExtraLarge}
                fontWeight={fontSemiBold}
                display="flex"
                alignItems="center"
                color={gray100}
              >
                <Box pr={2}>
                  <NotificationSuccessIcon width={40} />
                </Box>
                <Box id="topup-confirmation_modal-title-completed">
                  {t(
                    'containers.deposit_money.topup_tab.topup_in_progress.completed'
                  )}
                </Box>
              </Box>
            </Box>
          </Dialog.Title>
          <Dialog.Content>
            <Box
              fontSize={fontSize}
              id="topup-confirmation_modal-description-completed"
            >
              <Trans
                defaults={t(
                  'containers.deposit_money.topup_tab.topup_in_progress.completed_description',
                  {
                    benefitType: t(
                      `containers.deposit_money.topup_tab.confirmation_modal.benefits.${benefitType}`
                    )
                  }
                )}
                components={{
                  bold: <SemiBold />
                }}
              />
            </Box>
          </Dialog.Content>
        </Box>
      )}
      {isFail && (
        <Dialog.Title>
          <Box display="flex" flexDirection="column" alignItems="start">
            <Box
              fontSize={sizeExtraExtraLarge}
              fontWeight={fontSemiBold}
              display="flex"
              alignItems="center"
            >
              <Box pr={2}>
                <NotificationErrorIcon />
              </Box>
              {t('messages.error')}
            </Box>
          </Box>
        </Dialog.Title>
      )}

      <Dialog.Actions>
        <Box display={'flex'} flexDirection="row" justifyContent={'end'}>
          <Button
            id={'topup-onboarding_modal-next'}
            type="button"
            onClick={handleNext}
            minWidth={160}
          >
            {isSuccess && t('components.onboarding.buttons.next')}
            {isFail && t('components.error_popup.close_button')}
          </Button>
        </Box>
      </Dialog.Actions>
    </Box>
  );
};
